import React from 'react';
import { Button, TextInput } from '@mantine/core';
import { Button as SharedButton } from '../../../../../components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping, faCheck, faChevronLeft, faChevronRight, faGripLines, faGripLinesVertical, faPen, faPlus, faTableCellsLarge, faVectorSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { AreaView } from '../../../../../services/areas/areas.service';
import { useAreaBarController } from './useAreaBarController';
import classes from './AreaBar.module.scss';

type AreaBarProps = {
  areas: AreaView[];
  activeId: number | null;
  editing: boolean;
  floorBackground?: 'white' | 'image' | 'primary';
  isGridVisible?: boolean;
  canPickSizeMaster?: boolean;
  isPickSizeMasterActive?: boolean;
  onTogglePickSizeMaster?: () => void;
  canAlignSelectedTables?: boolean;
  onAlignSelectedTablesRow?: () => void;
  onAlignSelectedTablesColumn?: () => void;
  onSelect: (areaId: number) => void;
  onFloorBackgroundChange?: (floorBackground: 'white' | 'image' | 'primary') => void;
  onToggleGrid?: () => void;
  onAdd: (name: string) => void;
  onRename: (areaId: number, name: string) => void;
  onReorder: (areaId: number, otherAreaId: number) => void;
  onToggleEnabled: (area: AreaView) => void;
  onExitEdit: () => void;
  onOpenTakeAway?: () => void;
  onOpenQuickOrder?: () => void;
};

const AreaBarComponent: React.FC<AreaBarProps> = ({
  areas,
  activeId,
  editing,
  floorBackground = 'white',
  isGridVisible = false,
  canPickSizeMaster = false,
  isPickSizeMasterActive = false,
  onTogglePickSizeMaster,
  canAlignSelectedTables = false,
  onAlignSelectedTablesRow,
  onAlignSelectedTablesColumn,
  onSelect,
  onFloorBackgroundChange,
  onToggleGrid,
  onAdd,
  onRename,
  onReorder,
  onToggleEnabled,
  onExitEdit,
  onOpenTakeAway,
  onOpenQuickOrder,
}) => {
  const {
    inputRef,
    inlineMode,
    draft,
    setDraft,
    visibleAreas,
    orderedAreasLength,
    activeArea,
    activeOrderIndex,
    closeInline,
    openAdd,
    openRename,
    confirmInline,
    moveActiveArea,
    handleInputKeyDown,
  } = useAreaBarController({
    areas,
    activeId,
    editing,
    onAdd,
    onRename,
    onReorder,
  });

  const renderInlineField = (placeholder: string) => (
    <div className={classes.inline}>
      <TextInput
        ref={inputRef}
        data-testid="area-bar-inline-input"
        unstyled
        classNames={{ input: classes.inlineInput }}
        value={draft}
        placeholder={placeholder}
        maxLength={120}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onKeyDown={handleInputKeyDown}
        onBlur={closeInline}
      />
      <Button
        unstyled
        type="button"
        data-testid="area-bar-inline-confirm"
        className={`${classes.inlineButton} ${classes.inlineConfirm}`}
        onMouseDown={(event) => event.preventDefault()}
        onClick={confirmInline}
        aria-label="אישור"
      >
        <FontAwesomeIcon icon={faCheck as IconProp} />
      </Button>
      <Button
        unstyled
        type="button"
        data-testid="area-bar-inline-cancel"
        className={`${classes.inlineButton} ${classes.inlineCancel}`}
        onMouseDown={(event) => event.preventDefault()}
        onClick={closeInline}
        aria-label="ביטול"
      >
        <FontAwesomeIcon icon={faXmark as IconProp} />
      </Button>
    </div>
  );

  return (
    <div className={classes.bar} dir="rtl">
      <div className={classes.tabs}>
        {visibleAreas.map((area) => {
          const isActive = area.id === activeId;
          if (inlineMode === 'rename' && isActive) {
            return <React.Fragment key={area.id}>{renderInlineField('שם האזור')}</React.Fragment>;
          }
          const isDisabled = editing && !area.isActive;
          return (
            <Button
              key={area.id}
              unstyled
              type="button"
              data-testid={`area-bar-tab-${area.id}`}
              onClick={() => onSelect(area.id)}
              className={`${classes.tab} ${isActive ? classes.tabActive : ''} ${
                isDisabled ? classes.tabDisabled : ''
              }`}
            >
              {area.name}
            </Button>
          );
        })}

        {editing && inlineMode === 'add' && renderInlineField('שם האזור החדש')}

        {editing && inlineMode !== 'add' && (
          <Button unstyled type="button" data-testid="area-bar-add" className={classes.add} onClick={openAdd} aria-label="הוסף אזור">
            <FontAwesomeIcon icon={faPlus as IconProp} />
          </Button>
        )}
      </div>

      <div className={classes.spacer} />

      {onOpenTakeAway && !editing && (
        <Button
          unstyled
          type="button"
          data-testid="area-bar-take-away"
          className={classes.takeAwayButton}
          onClick={onOpenTakeAway}
          aria-label="הזמנת לקחת"
        >
          <span>לקחת</span>
          <FontAwesomeIcon icon={faBagShopping as IconProp} />
        </Button>
      )}

      {onOpenQuickOrder && !editing && (
        <Button
          unstyled
          type="button"
          data-testid="area-bar-quick-order"
          className={classes.quickOrderButton}
          onClick={onOpenQuickOrder}
          aria-label="הזמנה מהירה"
        >
          <span className={classes.quickOrderLabel}>ה. מהירה</span>
          <span className={classes.quickOrderIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9.97 1 H18.11 L13.68 10.1 L17.56 10.51 L7.36 23 L10.34 12.16 H5.9 Z" />
            </svg>
          </span>
        </Button>
      )}

      {editing && (
        <div className={classes.tools}>
          <div className={classes.floorBackgroundSelector} role="group" aria-label="רקע אזור">
            <Button
              unstyled
              type="button"
              data-testid="area-bar-floor-white"
              className={`${classes.floorBackgroundOption} ${classes.floorBackgroundWhite} ${floorBackground === 'white' ? classes.floorBackgroundOptionActive : ''}`}
              onClick={() => onFloorBackgroundChange?.('white')}
              aria-label="רקע לבן"
              aria-pressed={floorBackground === 'white'}
              title="רקע לבן"
            />
            <Button
              unstyled
              type="button"
              data-testid="area-bar-floor-image"
              className={`${classes.floorBackgroundOption} ${classes.floorBackgroundImage} ${floorBackground === 'image' ? classes.floorBackgroundOptionActive : ''}`}
              onClick={() => onFloorBackgroundChange?.('image')}
              aria-label="רקע תמונה"
              aria-pressed={floorBackground === 'image'}
              title="רקע תמונה"
              style={{ backgroundImage: "url('/option2.png')" }}
            />
            <Button
              unstyled
              type="button"
              data-testid="area-bar-floor-primary"
              className={`${classes.floorBackgroundOption} ${classes.floorBackgroundPrimary} ${floorBackground === 'primary' ? classes.floorBackgroundOptionActive : ''}`}
              onClick={() => onFloorBackgroundChange?.('primary')}
              aria-label="רקע ראשי"
              aria-pressed={floorBackground === 'primary'}
              title="רקע ראשי"
              style={{ backgroundImage: "url('/option3.png')" }}
            />
          </div>
          {inlineMode === 'rename' && (
            <>
              <Button
                unstyled
                type="button"
                data-testid="area-bar-move-right"
                className={classes.iconButton}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => moveActiveArea(-1)}
                disabled={!activeArea || activeOrderIndex <= 0}
                aria-label="הזז ימינה"
              >
                <FontAwesomeIcon icon={faChevronRight as IconProp} />
              </Button>
              <Button
                unstyled
                type="button"
                data-testid="area-bar-move-left"
                className={classes.iconButton}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => moveActiveArea(1)}
                disabled={!activeArea || activeOrderIndex === -1 || activeOrderIndex >= orderedAreasLength - 1}
                aria-label="הזז שמאלה"
              >
                <FontAwesomeIcon icon={faChevronLeft as IconProp} />
              </Button>
            </>
          )}
          <Button
            unstyled
            type="button"
            data-testid="area-bar-rename"
            className={classes.iconButton}
            onClick={openRename}
            disabled={!activeArea}
            aria-label="שנה שם אזור"
          >
            <FontAwesomeIcon icon={faPen as IconProp} />
          </Button>
          <Button
            unstyled
            type="button"
            data-testid="area-bar-size-master"
            className={`${classes.iconButton} ${isPickSizeMasterActive ? classes.iconButtonActive : ''}`}
            onClick={onTogglePickSizeMaster}
            disabled={!canPickSizeMaster}
            aria-label="בחר שולחן מקור לגודל"
            aria-pressed={isPickSizeMasterActive}
            title="העתק גודל משולחן נבחר"
          >
            <FontAwesomeIcon icon={faVectorSquare as IconProp} />
          </Button>
          <Button
            unstyled
            type="button"
            data-testid="area-bar-align-row"
            className={classes.iconButton}
            onClick={onAlignSelectedTablesRow}
            disabled={!canAlignSelectedTables}
            aria-label="יישר שורה"
            title="יישור לשורה"
          >
            <FontAwesomeIcon icon={faGripLines as IconProp} />
          </Button>
          <Button
            unstyled
            type="button"
            data-testid="area-bar-align-column"
            className={classes.iconButton}
            onClick={onAlignSelectedTablesColumn}
            disabled={!canAlignSelectedTables}
            aria-label="יישר עמודה"
            title="יישור לעמודה"
          >
            <FontAwesomeIcon icon={faGripLinesVertical as IconProp} />
          </Button>
          <Button
            unstyled
            type="button"
            data-testid="area-bar-toggle-grid"
            className={`${classes.iconButton} ${isGridVisible ? classes.iconButtonActive : ''}`}
            onClick={onToggleGrid}
            aria-label={isGridVisible ? 'הסתר רשת יישור' : 'הצג רשת יישור'}
            aria-pressed={isGridVisible}
            title="רשת יישור"
          >
            <FontAwesomeIcon icon={faTableCellsLarge as IconProp} />
          </Button>
          <Button
            unstyled
            type="button"
            data-testid="area-bar-toggle"
            className={`${classes.toggle} ${activeArea?.isActive ? classes.toggleOn : ''}`}
            onClick={() => activeArea && onToggleEnabled(activeArea)}
            disabled={!activeArea}
            aria-label={activeArea?.isActive ? 'השבת אזור' : 'הפעל אזור'}
            aria-pressed={activeArea?.isActive ?? false}
          >
            <span className={classes.knob} />
          </Button>
        </div>
      )}

      {editing && (
        <SharedButton variant="save" size="sm" onClick={onExitEdit} dataTestId="area-bar-finish-edit">
          סיים עריכה
        </SharedButton>
      )}
    </div>
  );
};

export const AreaBar = React.memo(AreaBarComponent);
